import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, User as UserIcon, LayoutDashboard } from "lucide-react";
import { useI18n, LANGS } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_78adfbdd-52b0-4e44-92bc-80f4766e6ce9/artifacts/ls9rp1yc_ChatGPT%20Image%20Sep%203%2C%202026%2C%2009_24_57%20AM.png";

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = (e) => {
    e?.preventDefault?.();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 20);
    }
    setOpen(false);
  };

  const links = [
    { to: "/woningen", label: t("nav.properties"), id: "nav-properties" },
    { to: "/hoe-het-werkt", label: t("nav.how"), id: "nav-how" },
    { to: "/woning-verkopen", label: t("nav.sellers"), id: "nav-sellers" },
    { to: "/werk-bij-ons", label: t("nav.careers"), id: "nav-careers" },
    { to: "/over-ons", label: t("nav.about"), id: "nav-about" },
    { to: "/contact", label: t("nav.contact"), id: "nav-contact" },
  ];

  return (
    <header className="wv-glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="h-[72px] flex items-center justify-between gap-4">
          <a
            href="/"
            onClick={goHome}
            data-testid="nav-logo-home"
            aria-label={t("back_home")}
            className="flex items-center gap-2 group cursor-pointer shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="Woon Verder"
              className="h-11 sm:h-12 w-auto transition-transform group-hover:scale-[1.03]"
              draggable="false"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className="px-3.5 py-2 text-sm font-semibold text-slate-700 hover:text-[color:var(--wv-navy)] rounded-lg hover:bg-slate-50 transition-all"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                data-testid="lang-switcher-btn"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 hover:text-[color:var(--wv-navy)] rounded-lg hover:bg-slate-50 transition-all uppercase tracking-wider"
              >
                {lang} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50 flex items-center justify-between ${lang === l.code ? "text-[color:var(--wv-green-dark)] font-semibold" : "text-slate-700"}`}
                    >
                      <span>{l.name}</span>
                      <span className="font-mono-num text-xs opacity-60">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!user ? (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login-btn"
                  className="hidden sm:inline-flex px-3.5 py-2 text-sm font-semibold text-slate-700 hover:text-[color:var(--wv-navy)] rounded-lg hover:bg-slate-50 transition-all"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/woning-aanmelden"
                  data-testid="nav-cta-submit"
                  className="hidden md:inline-flex wv-btn-outline text-sm"
                >
                  {t("cta.submit_property")}
                </Link>
                <Link
                  to="/woning-zoeken"
                  data-testid="nav-cta-search"
                  className="hidden md:inline-flex wv-btn-green text-sm"
                >
                  {t("cta.free_profile")}
                </Link>
              </>
            ) : (
              <div className="relative">
                <button
                  data-testid="nav-user-menu-btn"
                  onClick={() => setUserOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-[color:var(--wv-navy)] text-white grid place-items-center text-xs font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 hidden sm:block" />
                </button>
                {userOpen && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{user.role}</p>
                      <p className="text-sm text-slate-800 truncate">{user.email}</p>
                    </div>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        data-testid="nav-admin-link"
                        onClick={() => setUserOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"
                      >
                        <LayoutDashboard className="w-4 h-4" /> {t("nav.admin_dashboard")}
                      </Link>
                    )}
                    <Link
                      to="/verkoper"
                      data-testid="nav-seller-dash-link"
                      onClick={() => setUserOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"
                    >
                      <UserIcon className="w-4 h-4" /> {t("nav.my_account")}
                    </Link>
                    <button
                      data-testid="nav-logout-btn"
                      onClick={() => { setUserOpen(false); logout(); navigate("/"); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 text-left"
                    >
                      <LogOut className="w-4 h-4" /> {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              data-testid="nav-mobile-menu-btn"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-50"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-4 border-t border-slate-200 pt-3 space-y-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`m-${l.id}`}
                className="block px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                {l.label}
              </Link>
            ))}
            {!user && (
              <div className="pt-2 space-y-2">
                <Link to="/woning-aanmelden" onClick={() => setOpen(false)} className="block wv-btn-outline text-center" data-testid="m-cta-submit">
                  {t("cta.submit_property")}
                </Link>
                <Link to="/woning-zoeken" onClick={() => setOpen(false)} className="block wv-btn-green text-center" data-testid="m-cta-search">
                  {t("cta.free_profile")}
                </Link>
                <Link to="/login" onClick={() => setOpen(false)} className="block text-center py-2 text-sm font-semibold text-slate-700" data-testid="m-nav-login">
                  {t("nav.login")}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
